import React, { useEffect, useState } from "react";
import { changeCardStatus, deleteCard, getAllCards, getCards, getCardsBetweenDate } from "../../redux/cardSlice";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { style } from "../../Styles";
import CustomTableComponents from "../CommonComponents/CustomTableComponents";
import { Card, message } from "antd";
import { MinusOutlined } from "@ant-design/icons";
import Header from "../../global/Header/Header";
import dayjs, { Dayjs } from "dayjs";
import isBetween from 'dayjs/plugin/isBetween';
import ActionButtonWithChangeStatus from "../CommonComponents/ActionButtonWithChangeStatus";
import debounce from "lodash.debounce";
import { set } from "lodash";

const today = dayjs().startOf("day");
const tomorrow = dayjs().add(1, "day").startOf("day");

const Cards = () => {
  const { t } = useTranslation();
  dayjs.extend(isBetween);

  const columns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => {
        // Calculate the actual row number based on current page and page size
        const currentPage = cards?.currentPage || 1;
        const pageSize = cards?.pageSize || 10;
        return <span>{(currentPage - 1) * pageSize + index + 1}</span>;
      },
      width: 60,
      show: true,
    },
    {
      title: t("cardScreen.table.cardNumber"),
      key: "cardNumber",
      render: (record) => {
        return <span>{record.cardNumber}</span>;
      },
      sorter: (a, b) => a.cardNumber.length - b.cardNumber.length,
      show: true,
    },
    {
      title: t("cardScreen.table.totalNumberOfTrips"),
      key: "totalNumberOfTrips",
      render: (record) => {
        return record.totalNumberOfTrips ? (
          <span>{record.totalNumberOfTrips}</span>
        ) : (
          0
        );
      },
      sorter: (a, b) => a.totalNumberOfTrips - b.totalNumberOfTrips,
      show: true,
    },
    {
      title: t("cardScreen.table.usedTrips"),
      key: "usedTrips",
      render: (record) => {
        return record.usedTrips ? (
          <span>{record.usedTrips}</span>
        ) : (
          0
        );
      },
      sorter: (a, b) => a.usedTrips - b.usedTrips,
      show: true,
    },
    {
      title: t("cardScreen.table.status"),
      key: "active",
      render: (record) => {
        return (
          <span style={style.dataContainer}>
            {record.active ? (
              <div style={style.statusActif}>{t("statuses.active")}</div>
            ) : (
              <div
                style={{
                  ...style.statusInactif,
                  capitalize: "capitalize",
                }}
              >
                {t("statuses.inactive")}
              </div>
            )}
          </span>
        );
      },
      sorter: (a, b) => a.active.length - b.active.length,
      show: true,
    },
    {
      title: t("cardScreen.table.actions"),
      key: "actions",
      render: (text, record) => (
        <ActionButtonWithChangeStatus
          record={record}
          onDelete={() => handleDeleteSpecialCard(record.id)}
          onRowClick={null}
          displayEditButton={false}
          onChangeStatus={() => handleChangeStatus(record)}
        />
      ),
      show: true,
    },
  ];

  const [filter, setFilter] = useState("");
  const [isMakingApiCall, setMakingApiCall] = useState(true);
  const [dateFilter, setDateFilter] = useState([today, tomorrow]);
  const [csvData, setCsvData] = useState([])
  const [csvHeaders, setCsvHeaders] = useState([
    {
      label: t("cardScreen.table.index"),
      key: "index",
    },
    {
      label: t("cardScreen.table.cardNumber"),
      key: "cardNumber",
    },
    {
      label: t("cardScreen.table.totalNumberOfTrips"),
      key: "totalNumberOfTrips",
    },
    {
      label: t("cardScreen.table.usedTrips"),
      key: "usedTrips",
    },
    {
      label: t("cardScreen.table.status"),
      key: "active",
    },
  ]);

  const dispatch = useDispatch();
  const { cards } = useSelector((state) => state.cards);
  const { allCards } = useSelector((state) => state.cards);

  const fetchCards = async (page = 1, pageSize = 10) => {
    setMakingApiCall(true);
    try {
      dispatch(getCards({page, pageSize, search:filter}))
      .unwrap()
      .then(() => {
        setMakingApiCall(false);
      })
      .catch(() => {
        setMakingApiCall(false);
      });
    } catch (error) {
      console.error("Error fetching cards", error);
    } finally {
      setMakingApiCall(false);
    }
  };

  const fetchAllCards = async () => {
    setMakingApiCall(true);
    try {
      const data = await dispatch(getAllCards({ search: filter })).unwrap();
      setMakingApiCall(false);
      setCsvData(data)
      return data; // Return the data to the caller
    } catch (error) {
      setMakingApiCall(false);
      console.error("Error fetching cards", error); // Re-throw to let the caller handle it
    }
  };

  const fetchCardsOld = () => {
    setMakingApiCall(true);
        // Set start to 00:00
        const start = dateFilter[0]
          .startOf("day")
          .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    
        // Set end to 23:59
        const end = dateFilter[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
        dispatch(
          getCardsBetweenDate({
            start: start !== undefined ? start : null,
            end: end !== undefined ? end : null,
          })
        )
          .unwrap()
          .then((data) => {
            setMakingApiCall(false)})
          .catch(() => setMakingApiCall(false));
  };

  const handleDeleteSpecialCard = (id) => {
    dispatch(deleteCard(id))
      .unwrap()
      .then(() => {
        message.success(t("cardScreen.messages.cardDeletedSuccessfully"));
        fetchCards();
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  const handleChangeStatus = (record) => {
    dispatch(
      changeCardStatus({
        id: record.id,
        status: !record.active,
      })
    )
      .unwrap()
      .then(() => {
        message.success(
          t("cardScreen.messages.cardStatusUpdatedSuccessfully")
        );
      })
      .catch((e) => {
        message.error(e.message);
      });
  };

  useEffect(() => {
    const delayedSearch = debounce(() => {
      fetchCards();
    }, 500); // Wait 500ms after the last keystroke

    delayedSearch();
    return delayedSearch.cancel;
  }, [dispatch, filter]);

  const handleGenerateCsv = async (event, done) => {
    try {
      setCsvHeaders([
        {
          label: t("cardScreen.table.cardNumber"),
          key: "cardNumber",
        },
        {
          label: t("cardScreen.table.totalNumberOfTrips"),
          key: "totalNumberOfTrips",
        },
        {
          label: t("cardScreen.table.usedTrips"),
          key: "usedTrips",
        },
        {
          label: t("cardScreen.table.status"),
          key: "active",
        },
      ]);

      const data = await fetchAllCards(); // Fetch latest data
      setCsvData(data); // Update local state (optional, for consistency)
      done(true); // Trigger download after state is set
    } catch (error) {
      console.error("Error generating CSV:");
      done(false); // Cancel export on error
    }
  };
  
  /*const matchesFilter = (item, filter) => {
    const filterLower = filter?.trim().toLowerCase();
    return (
      item?.cardNumber
      .toString()
      .toLowerCase()
      ?.includes(filterLower.toLowerCase()) ||
    item?.usedTrips?.toString().toLowerCase().includes(filterLower.toLowerCase()) ||
    item?.totalNumberOfTrips
      .toString()
      .toLowerCase()
      ?.includes(filterLower.toLowerCase()) ||
    item?.transaction?.bundle?.name
      .toLowerCase()
      ?.includes(filterLower.toLowerCase()) ||  
    item?.active.toString().toLowerCase()?.includes(filterLower.toLowerCase())
    );
  };
  
  const filterCards = (cards, filter, dateFilter) => {
    return cards?.data?.filter((item) => {
      const itemDate = dayjs(item?.created).format('YYYY-MM-DD');
      //const isWithinRange = isDateWithinRange(dayjs(itemDate), dateFilter);
      return matchesFilter(item, filter);
    });
  };

  const filtredData = filterCards(cards, filter, dateFilter);


  const onFilterByDate = (date, dateString) => {

    const startDate = dayjs(date[0]);
    const endDate = dayjs(date[1]);
    setDateFilter([startDate, endDate]);
  }*/

  return (
    <>
      <Header
        onAddButtonClick={() => {}}
        onSearch={(e) => {
          setFilter(e.target.value);
        }}
        searchPlaceholder={t("cardScreen.header.search")}
        addButtonTextColor={"#ffffff"}
        onReload={fetchCards}
        showAddButton={false}
        BreadcrumbItems={[
          {
            title: t("cardScreen.header.title"),
          },
        ]}
        showBreadcrumb={true}
        showExportCSV={true}
        csvHeaders={csvHeaders}
        csvData={allCards?.data}
        generateCSVfn={handleGenerateCsv}
        csvDataMiddleFileName={"Cards"}
        fetchDataPdf={fetchAllCards}
       /*  showFilterByDate={false}
        onFilterByDate={(date, dateString) => onFilterByDate(date, dateString)}
        dateRange={dateFilter} */
      />
      <Card style={{ margin: 16, height: "65vh" }}>
        <CustomTableComponents
          isMakingApiCall={isMakingApiCall}
          columns={columns.filter((column) => column.show)}
          dataArray={cards?.data}
          paginationMeta={cards?.totalCards}
          onPageChange={(page, pageSize) => fetchCards(page, pageSize)}
          handleDoubleClickRow={() => {}}
          handleSelectedRow={() => {}}
        />
      </Card>
    </>
  );
};

export default Cards;
